import React, { useCallback, useEffect, useState } from 'react';
import { api, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { History, RefreshCw, Loader2, CalendarX2, LogIn, AlertTriangle } from 'lucide-react';

/**
 * Control rastreo — SOLO administradores (la ruta va bajo <SoloAdmin> y el
 * backend exige rol admin). Dos vistas:
 *  - Actividad: todo movimiento que modifica algo (quién, qué, cuándo) + logins.
 *  - Citas anuladas: listado con quién la anuló, cuándo y el motivo.
 */

function fechaHora(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${formatDate(iso.slice(0, 10))} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return iso; }
}

const RANGOS = [
  { dias: 1, label: 'Hoy' },
  { dias: 7, label: '7 días' },
  { dias: 30, label: '30 días' },
  { dias: 90, label: '90 días' },
  { dias: 365, label: '1 año' },
];

function TablaActividad() {
  const [items, setItems] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [q, setQ] = useState('');
  const [usuario, setUsuario] = useState('todos');
  const [dias, setDias] = useState(7);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    setBusy(true);
    try {
      const params = { dias, limit: 300 };
      if (q.trim()) params.q = q.trim();
      if (usuario !== 'todos') params.usuario = usuario;
      const res = await api.get('/api/audit', { params });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { setItems([]); } finally { setBusy(false); }
  }, [q, usuario, dias]);

  useEffect(() => { const t = setTimeout(cargar, 250); return () => clearTimeout(t); }, [cargar]);
  useEffect(() => {
    api.get('/api/audit/usuarios').then((r) => setUsuarios(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  return (
    <div className="cp-card overflow-hidden">
      <div className="p-3 flex flex-wrap items-center gap-2 border-b border-border">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar acción, usuario, paciente…"
          className="w-64"
          data-testid="rastreo-buscar"
        />
        <Select value={usuario} onValueChange={setUsuario}>
          <SelectTrigger className="w-48" data-testid="rastreo-usuario"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los usuarios</SelectItem>
            {usuarios.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          {RANGOS.map((r) => (
            <Button key={r.dias} size="sm" variant={dias === r.dias ? 'default' : 'ghost'}
              className={dias === r.dias ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
              onClick={() => setDias(r.dias)}>
              {r.label}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={cargar} disabled={busy} className="ml-auto" data-testid="rastreo-refrescar">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {items === null ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={History} title="Sin movimientos" description="No hay actividad registrada con esos filtros." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left">
              <tr>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Cuándo</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Quién</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Qué</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const fallo = (it.status || 0) >= 400;
                const esLogin = (it.accion || '').toLowerCase().includes('login') || it.accion === 'Inicio de sesión';
                return (
                  <tr key={it.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="rastreo-fila">
                    <td className="py-2 px-4 text-xs tabular-nums whitespace-nowrap">{fechaHora(it.ts)}</td>
                    <td className="py-2 px-4 font-medium whitespace-nowrap">
                      {it.user_name || '—'}
                      {it.role && <span className="ml-1.5 text-[10px] text-muted-foreground">{it.role}</span>}
                    </td>
                    <td className="py-2 px-4">
                      <span className="inline-flex items-center gap-1.5">
                        {esLogin && <LogIn className="w-3.5 h-3.5 text-muted-foreground" />}
                        {it.accion}
                      </span>
                      {Array.isArray(it.cambios) && it.cambios.length > 0 ? (
                        <div className="mt-1 space-y-0.5">
                          {it.cambios.map((c, i) => (
                            <div key={i} className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground/80">{c.etiqueta || c.campo}:</span>{' '}
                              <span className="line-through decoration-red-400/60">{c.antes}</span>
                              {' → '}
                              <span className="text-emerald-800 dark:text-emerald-400">{c.despues}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        it.detalle && <span className="ml-1.5 text-xs text-muted-foreground">{it.detalle}</span>
                      )}
                    </td>
                    <td className="py-2 px-4">
                      {fallo ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-700">
                          <AlertTriangle className="w-3.5 h-3.5" /> Rechazado ({it.status})
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-700">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TablaAnuladas() {
  const [items, setItems] = useState(null);
  const [dias, setDias] = useState(90);

  useEffect(() => {
    let off = false;
    api.get('/api/audit/citas-anuladas', { params: { dias } })
      .then((r) => { if (!off) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!off) setItems([]); });
    return () => { off = true; };
  }, [dias]);

  return (
    <div className="cp-card overflow-hidden">
      <div className="p-3 flex items-center gap-1 border-b border-border">
        {RANGOS.filter((r) => r.dias >= 7).map((r) => (
          <Button key={r.dias} size="sm" variant={dias === r.dias ? 'default' : 'ghost'}
            className={dias === r.dias ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
            onClick={() => setDias(r.dias)}>
            {r.label}
          </Button>
        ))}
      </div>
      {items === null ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarX2} title="Sin citas anuladas" description="No hay anulaciones en ese periodo." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left">
              <tr>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Paciente</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Cita</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Citada por</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Anulada por</th>
                <th className="py-2.5 px-4 font-semibold text-[#13231E]">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="rastreo-anulada">
                  <td className="py-2 px-4 font-medium">{c.patient_name || '—'}</td>
                  <td className="py-2 px-4 text-xs">
                    {formatDate(c.date)} {c.start} · {c.concept_name || '—'}
                    <span className="text-muted-foreground"> · {c.agenda_name}</span>
                  </td>
                  <td className="py-2 px-4 text-xs">
                    {c.created_by_name || '—'}
                    {c.created_at && <span className="text-muted-foreground"> · {fechaHora(c.created_at)}</span>}
                  </td>
                  <td className="py-2 px-4 text-xs text-red-700 whitespace-nowrap">
                    {c.cancelled_by_name || '—'}
                    {c.cancelled_at && <span className="text-muted-foreground"> · {fechaHora(c.cancelled_at)}</span>}
                  </td>
                  <td className="py-2 px-4 text-xs">{c.cancel_reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ControlRastreoPage() {
  const [tab, setTab] = useState('actividad');
  return (
    <div>
      <PageHeader
        title="Control rastreo"
        subtitle="Quién ha hecho qué y cuándo. Solo visible para administradores."
      />
      <div className="flex gap-1 mb-4">
        <Button size="sm" variant={tab === 'actividad' ? 'default' : 'ghost'}
          className={tab === 'actividad' ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
          onClick={() => setTab('actividad')} data-testid="rastreo-tab-actividad">
          <History className="w-4 h-4 mr-1.5" /> Actividad
        </Button>
        <Button size="sm" variant={tab === 'anuladas' ? 'default' : 'ghost'}
          className={tab === 'anuladas' ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
          onClick={() => setTab('anuladas')} data-testid="rastreo-tab-anuladas">
          <CalendarX2 className="w-4 h-4 mr-1.5" /> Citas anuladas
        </Button>
      </div>
      {tab === 'actividad' ? <TablaActividad /> : <TablaAnuladas />}
    </div>
  );
}
